import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../services/supabaseClient';
import axios from 'axios';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState('free');

  const publicSiteUrl = (process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin).replace(/\/+$/, '');

  useEffect(() => {
    // Get session from Supabase auth
    const getSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
      }
      
      setSession(session);
      setUser(session?.user || null);
      setLoading(false);
    };

    getSession();

    // Listen for changes to auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user || null);
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Sign up function
  const signUp = async (email, password, captchaToken) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Update email
  const updateEmail = async (newEmail) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Sign in function
  const signIn = async (email, password, captchaToken) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore errors (e.g. expired/invalid session) — we still want to clear local state
    }
    // Always clear local state regardless of whether the API call succeeded
    setSession(null);
    setUser(null);
    // Force redirect to login
    window.location.href = '/login';
    return { error: null };
  };

  // Password reset request
  const resetPassword = async (email, captchaToken) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${publicSiteUrl}/reset-password`,
        captchaToken: captchaToken || undefined,
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Fetch user tier whenever session changes
  useEffect(() => {
    const fetchTier = async () => {
      if (!session?.access_token) {
        setTier('free');
        return;
      }
      try {
        const res = await axios.get('/api/usage', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const t = res?.data?.tier;
        setTier(typeof t === 'string' && t ? t : 'free');
      } catch {
        setTier('free');
      }
    };
    fetchTier();
  }, [session]);

  const isPremium = tier === 'premium';

  // Check if current user is admin
  // SECURITY: Only trust app_metadata (server-only, not user-writable)
  const isAdmin = () => {
    if (!user) return false;
    if (user.app_metadata?.isAdmin === true) return true;
    const role = user.app_metadata?.role;
    if (role && String(role).toLowerCase() === 'admin') return true;
    return false;
  };

  // Value object that will be passed to any consuming components
  const value = {
    user,
    session,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateEmail,
    updatePassword,
    isAdmin,
    tier,
    isPremium,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
